import { Injectable } from '@angular/core';
import {
  Observable,
  from,
  throwError,
  merge
} from 'rxjs';
import { catchError, map, scan, shareReplay } from 'rxjs/operators';
import { SupabaseClientService } from './db/supabase-client';
import { Tables } from '@models/database.types';
import { WorkflowJobRun } from '@app/core/models';
import { WorkflowJobRunStatus } from '@models/workflow.types';
import { AuthService } from "@services/auth";
export type WorkflowJobRunLog = Tables<'workflow_job_run_logs'>;

export interface WorkflowJobRunsQueryParams {
  job_id?: string;
  status?: WorkflowJobRunStatus;
  order_by?: 'created_at' | 'started_at' | 'completed_at' | 'updated_at';
  order_direction?: 'asc' | 'desc';
}

@Injectable({
  providedIn: 'root'
})
export class WorkflowJobRunsService {
  private static readonly TABLE = 'workflow_job_runs';

  constructor(private supabaseClient: SupabaseClientService, private authService: AuthService) { }

  /**
   * Get average duration of all runs where duration > 0
   */
  getAverageDuration(): Observable<number> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select('duration')
          .gt('duration', 0);

        if (error) {
          throw error;
        }

        if (!data || data.length === 0) {
          return 0;
        }

        const totalDuration = data.reduce((sum, run) => sum + (run.duration || 0), 0);
        return totalDuration / data.length;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to get average duration:', error);
        return throwError(() => error);
      })
    );
  }

  list(params: WorkflowJobRunsQueryParams = {}): Observable<WorkflowJobRun[]> {
    const {
      job_id,
      status,
      order_by = 'created_at',
      order_direction = 'desc'
    } = params;

    return from(
      (async () => {
        let query = this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select(`
            *,
            agent:agents!agent_id (
              id,
              name
            )
          `)
          .order(order_by, { ascending: order_direction === 'asc' });

        if (job_id) {
          query = query.eq('job_id', job_id);
        }
        if (status) {
          query = query.eq('status', status);
        }

        const { data, error } = await query;
        if (error) {
          throw error;
        }
        return (data as WorkflowJobRun[]) ?? [];
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to load workflow job runs:', error);
        return throwError(() => error);
      })
    );
  }

  getById(id: string): Observable<WorkflowJobRun | null> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select(`
            *
          `)
          .eq('id', id)
          .single();

        if (error) {
          throw error;
        }
        return data as WorkflowJobRun | null;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to load workflow job run:', error);
        return throwError(() => error);
      })
    );
  }

  getLogs(runId: string): Observable<WorkflowJobRunLog[]> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from('workflow_job_run_logs')
          .select('*')
          .eq('run_id', runId)
          .order('created_at', { ascending: true });

        if (error) {
          throw error;
        }
        return data as WorkflowJobRunLog[];
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to load workflow job run logs:', error);
        return throwError(() => error);
      })
    );
  }

  getLogsWithSubscription(runId: string): Observable<WorkflowJobRunLog[]> {
    return merge(
      this.subscribeToLogChanges(runId),
      this.getLogs(runId)
    )
      .pipe(
        scan((acc, curr) => {
          if (Array.isArray(curr)) {
            acc.unshift(...curr.filter(x => !acc.some(y => y.id === x.id)));
          } else {
            acc.push(curr as WorkflowJobRunLog);
          }

          return acc;
        }, [] as WorkflowJobRunLog[]),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  subscribeToLogChanges(runId: string): Observable<WorkflowJobRunLog> {
    return this.supabaseClient.subscribeToTableWithFilter('workflow_job_run_logs', 'run_id', runId, 'INSERT').pipe(
      map((payload) => payload.new)
    );
  }

  subscribeToWorkflowRunChanges(runId: string): Observable<WorkflowJobRun> {
    return this.supabaseClient.subscribeToTableWithFilter('workflow_job_runs', 'id', runId, 'UPDATE').pipe(
      map((payload) => payload.new as WorkflowJobRun)
    );
  }

  getLatestPlaygroundRun(): Observable<WorkflowJobRun | null> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select('*')
          .eq('type', 'playground')
          .eq('started_by', this.authService.user?.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // No rows found
            return null;
          }
          throw error;
        }
        return data as WorkflowJobRun;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to load latest playground run:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get the last job execution time (most recent completed_at)
   */
  getLastExecutionTime(): Observable<Date | null> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select('completed_at')
          .not('completed_at', 'is', null)
          .order('completed_at', { ascending: false })
          .limit(1)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // No rows found
            return null;
          }
          throw error;
        }

        return data?.completed_at ? new Date(data.completed_at) : null;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to get last execution time:', error);
        return throwError(() => error);
      })
    );
  }
  /**
   * Get executions count by day for the current month
   */
  getExecutionsByDay(): Observable<{ date: string; count: number }[]> {
    return from(
      (async () => {
        // Get first day of current month
        const now = new Date();
        const firstDayOfMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));

        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select('created_at')
          .gte('created_at', firstDayOfMonth.toISOString())
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Group by date
        const grouped = (data || []).reduce((acc: Record<string, number>, run: { created_at: string }) => {
          const date = new Date(run.created_at).toISOString().split('T')[0];
          acc[date] = (acc[date] || 0) + 1;
          return acc;
        }, {});

        // Fill all days of current month with 0
        const result: { date: string; count: number }[] = [];
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), day)).toISOString().split('T')[0];
          result.push({
            date,
            count: grouped[date] || 0
          });
        }

        return result;
      })()
    ).pipe(
      catchError(error => {
        console.error('Error loading executions by day:', error);
        return throwError(() => error);
      })
    );
  }
  /**
   * Get success/failure stats for today
   */
  getTodayStats(): Observable<{ completed: number; failed: number }> {
    return from(
      (async () => {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .select('status')
          .gte('created_at', startOfDay.toISOString())
          .in('status', ['completed', 'failed']);

        if (error) throw error;

        const stats = (data || []).reduce(
          (acc, run) => {
            if (run.status === 'completed') acc.completed++;
            else if (run.status === 'failed') acc.failed++;
            return acc;
          },
          { completed: 0, failed: 0 }
        );

        return stats;
      })()
    ).pipe(
      catchError(error => {
        console.error('Error loading today stats:', error);
        return throwError(() => error);
      })
    );
  }


  /**
   * Update run status (universal method for both workflow and playground)
   */
  updateRunStatus(runId: string, status: WorkflowJobRunStatus): Observable<void> {
    return from(
      (async () => {
        const updatePayload: Partial<{
          status: WorkflowJobRunStatus;
          updated_at: string;
          completed_at: string;
          error: string;
        }> = {
          status,
          updated_at: new Date().toISOString()
        };

        if (status === 'completed' || status === 'failed') {
          updatePayload.completed_at = new Date().toISOString();
        }

        const { error: dbError } = await this.supabaseClient.client
          .from(WorkflowJobRunsService.TABLE)
          .update(updatePayload)
          .eq('id', runId);

        if (dbError) throw dbError;
      })()
    ).pipe(
      catchError(err => {
        console.error('Failed to update run status:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Cancel run by setting status to 'cancelling' (universal for workflow and playground)
   * Backend will handle the actual cancellation
   */
  cancelRun(runId: string): Observable<void> {
    return this.updateRunStatus(runId, 'cancelling');
  }
}
